import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button as MuiButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Error as AlertCircleIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  documentIcon,
  RequireIcon as CustomRequireIcon,
} from "@ippon/shared-assets";
import { useSharedColors } from "../../index";
import CustomFileModal from "../Modal/CustomFileModal";
import CustomTruncateText from "../../TruncateText/CustomTruncateText";
import CustomFormCreateInput from "../../Inputs/FormCreateInput/CustomFormCreateInput";

export interface DocumentItem {
  name: string;
  url?: string;
  file?: File;
}

export interface CustomDocumentPreviewProps {
  name: string;
  label: string;
  register?: any;
  setValue?: any;
  watch?: any;
  errors?: any;
  width?: string;
  type?: "single" | "multiple";
  accept?: string;
  maxSize?: number;
  required?: boolean;
  defaultValue?: File[];
  existingDocuments?: string[]; // 既存のファイルURL配列
  onDownload?: (doc: DocumentItem) => void;
  onRemove?: () => void; // フォーム全体を削除するコールバック
  onDocumentDeleted?: (deletedUrl: string, index?: number) => void; // 既存ドキュメント削除時のコールバック
}

const CustomDocumentPreview: React.FC<CustomDocumentPreviewProps> = ({
  name,
  label,
  register,
  setValue,
  watch,
  errors = {},
  width = "100%",
  type = "multiple",
  accept = ".pdf",
  maxSize = 20,
  required = false,
  defaultValue = [],
  existingDocuments = [],
  onRemove,
  onDocumentDeleted,
}) => {
  const colors = useSharedColors();
  const [files, setFiles] = useState<File[]>(defaultValue || []);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    src: string;
    name: string;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const documentsProcessedRef = useRef(false); // existingDocuments が処理されたかどうか

  // 既存のドキュメント（URL）をDocumentItem形式に変換
  const prevExistingDocsRef = useRef<string[]>([]);
  React.useEffect(() => {
    if (existingDocuments && existingDocuments.length > 0) {
      // existingDocuments が更新されたかチェック
      const prevUrls = prevExistingDocsRef.current.sort().join(",");
      const currentUrls = existingDocuments.sort().join(",");

      // URLが変更された場合のみ処理（初回ロードまたは更新後）
      if (prevUrls !== currentUrls) {
        const existingItems: DocumentItem[] = existingDocuments.map((url) => {
          // URLからファイル名を抽出（クエリパラメータを除外）
          try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const parts = pathname.split("/");
            const lastPart = parts[parts.length - 1];
            return {
              name: decodeURIComponent(lastPart),
              url: url,
            };
          } catch {
            // URLのパースに失敗した場合は、単純な分割方法にフォールバック
            const pathWithoutQuery = url.split("?")[0];
            const fileName =
              pathWithoutQuery.split("/").pop() || "document.pdf";
            return {
              name: decodeURIComponent(fileName),
              url: url,
            };
          }
        });
        setDocuments(existingItems);
        setFiles([]);

        // フォームの値も即座に更新（既存URLのみ）
        if (setValue) {
          const existingUrls = existingItems
            .map((doc) => doc.url)
            .filter((url): url is string => !!url);
          const newValue = existingUrls.length > 0 ? existingUrls : null;

          // 更新後のデータ反映のため、強制的にsetValueを呼ぶ
          // 初回ロード時（prevUrls === ""）は shouldDirty: false
          // 更新後（prevUrls !== ""）も shouldDirty: false（既存データの反映なので）
          setValue(name, newValue, {
            shouldValidate: true, // バリデーションは実行
            shouldDirty: false, // 既存データの反映なので dirty にしない
          });
        }
      }
      prevExistingDocsRef.current = [...existingDocuments];
    } else {
      setDocuments([]);

      // 既存ドキュメントが空の場合の処理。
      //   - 「既存あり → 空」(削除) のときのみフォームを null にリセット
      //   - 「初回マウント時」も 1 回だけ null セットする (フォーム登録のため)
      // 「ずっと空のまま」(同じ空配列が再評価されただけ) のケースで setValue(null) を
      //  呼んでしまうと、ユーザーがアップロードしたばかりの File が消える事故になる。
      const prevUrls = prevExistingDocsRef.current.sort().join(",");
      if (prevUrls !== "" && setValue) {
        // 既存ドキュメントがあったが、今は空になった場合（削除された場合）
        setFiles([]);
        setValue(name, null, {
          shouldValidate: false,
          shouldDirty: false,
        });
      } else if (
        prevUrls === "" &&
        !documentsProcessedRef.current &&
        setValue
      ) {
        // 初回ロード時で既存ドキュメントがない場合、フォームに登録だけする (1 回のみ)
        setValue(name, null, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
      prevExistingDocsRef.current = [];
    }
    // existingDocuments が処理されたことをマーク
    documentsProcessedRef.current = true;
  }, [existingDocuments, setValue, name]);

  // 既存ドキュメントと新規ファイルを統合したリスト
  const allDocuments: (DocumentItem & { index: number; isNew: boolean })[] = [
    ...documents.map((doc, idx) => ({ ...doc, index: idx, isNew: false })),
    ...files.map((file, idx) => ({
      name: file.name,
      file: file,
      index: idx,
      isNew: true,
    })),
  ];

  // defaultValue は使用していないため、このuseEffectは無効化
  // フォームの値は handleFiles と existingDocuments の useEffect で管理

  // register for required rule only
  React.useEffect(() => {
    if (register) {
      register(name, {
        ...(required && {
          required: `${label}は必須です` as any,
          validate: (value: any) => {
            // 新規ファイルがある場合
            if (Array.isArray(value) && value.length > 0) return true;
            if (!Array.isArray(value) && value) return true;
            // 既存ドキュメントがある場合もOK
            if (documents.length > 0) return true;
            return `${label}は必須です`;
          },
        }),
      });
    }
  }, [register, name, required, label, documents.length]);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxSize * 1024 * 1024) {
        return `${file.name}は${maxSize}MBを超えています`;
      }
      const acceptedTypes = accept
        .split(",")
        .map((t) => t.trim().toLowerCase());
      const fileExtension =
        "." + (file.name.split(".").pop() || "").toLowerCase();
      if (!acceptedTypes.includes(fileExtension)) {
        return `${file.name}は対応していないファイル形式です`;
      }
      return null;
    },
    [accept, maxSize],
  );

  const handleFiles = useCallback(
    (newFiles: FileList | null) => {
      if (!newFiles) return;
      setUploadError(null);
      const fileArray = Array.from(newFiles);

      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          setUploadError(error);
          return;
        }
      }

      let updated: File[];
      if (type === "single") {
        updated = [fileArray[0]];
      } else {
        updated = [...files, ...fileArray];
      }

      setFiles(updated);
      if (setValue) {
        // 既存URL + 新規ファイルをフォームに保存
        const existingUrls = documents
          .map((doc) => doc.url)
          .filter((url): url is string => !!url);
        const combined = [...existingUrls, ...updated];
        setValue(name, combined, { shouldValidate: true, shouldDirty: true });
      }
    },
    [files, type, validateFile, name, setValue, documents],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // Reset input value to allow selecting the same file again
      e.target.value = "";
    },
    [handleFiles],
  );

  const removeFile = useCallback(
    (index: number) => {
      const updatedFiles = files.filter((_, i) => i !== index);
      setFiles(updatedFiles);
      if (setValue) {
        // 既存URL + 新規ファイルをフォームに保存
        const existingUrls = documents
          .map((doc) => doc.url)
          .filter((url): url is string => !!url);
        const combined = [...existingUrls, ...updatedFiles];
        setValue(name, combined.length > 0 ? combined : null, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      setUploadError(null);
      if (inputRef.current) inputRef.current.value = "";
    },
    [files, name, setValue, documents],
  );

  // 統合されたドキュメントリストから削除
  const removeDocument = useCallback(
    (index: number, isNew: boolean) => {
      if (isNew) {
        // 新規ファイルの削除
        removeFile(index);
      } else {
        // 既存ドキュメントの削除
        const deletedDoc = documents[index];
        const updatedDocuments = documents.filter((_, i) => i !== index);
        setDocuments(updatedDocuments);

        // 削除後の既存URL + 新規ファイルをフォームに保存
        if (setValue) {
          const remainingUrls = updatedDocuments
            .map((doc) => doc.url)
            .filter((url): url is string => !!url);
          const combined = [...remainingUrls, ...files];
          setValue(name, combined.length > 0 ? combined : null, {
            shouldDirty: true,
            shouldValidate: true,
          });

          // 削除されたことを記録（削除フラグ）
          // インデックスと一緒に記録して、同じURLの書類を区別できるようにする
          if (deletedDoc?.url) {
            const deletedFieldName = `${name}_deleted`;
            const currentDeleted = (watch && watch(deletedFieldName)) || [];
            // { url, originalIndex } の形式で記録
            setValue(
              deletedFieldName,
              [...currentDeleted, { url: deletedDoc.url, index }],
              {
                shouldDirty: true,
                shouldValidate: false,
              },
            );
          }
        }

        // 削除コールバックを呼び出し（親コンポーネントで管理）
        // インデックスも渡すように変更
        if (deletedDoc?.url && onDocumentDeleted) {
          onDocumentDeleted(deletedDoc.url, index);
        }
      }
    },
    [documents, removeFile, setValue, name, watch, onDocumentDeleted, files],
  );

  const hasError = errors && (errors as any)[name];

  const handlePreviewClick = (file?: File, doc?: DocumentItem) => {
    if (file) {
      const url = URL.createObjectURL(file);
      setSelectedDocument({ src: url, name: file.name });
      setModalOpen(true);
    } else if (doc?.url) {
      // 既存ドキュメント（URL）の場合もモーダルで表示
      setSelectedDocument({ src: doc.url, name: doc.name });
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    if (selectedDocument?.src.startsWith("blob:")) {
      URL.revokeObjectURL(selectedDocument.src);
    }
    setSelectedDocument(null);
  };

  return (
    <Box
      sx={{ width: { xs: "100%", sm: "100%", md: "600px" }, maxWidth: width }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pb: "1px",
          mb: "6px",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px !important",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            color: colors.text.primary,
            fontWeight: 700,
          }}
        >
          {label}
          {required && (
            <span style={{ display: "flex", marginLeft: "4px" }}>
              <CustomRequireIcon />
            </span>
          )}
        </Typography>
        {onRemove && (
          <IconButton
            size="small"
            onClick={() => {
              onRemove();
            }}
            sx={{
              color: "#3e3e3e",
              width: 20,
              height: 20,
              flexShrink: 0,
              ml: 1,
              mt: 0.3,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      {/* アップロードボタン（ファイルがない場合） */}
      {allDocuments.length === 0 && (
        <MuiButton
          variant="outlined"
          onClick={() => inputRef.current?.click()}
          sx={{
            width: "100%",
            height: 34,
            fontSize: "12px",
            borderWidth: 1,
            borderColor: "#D9D9D9",
            color: "#0B9DBD",
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            "&:hover": {
              borderColor: "#D9D9D9",
              bgcolor: "#FFFFFF",
            },
            "&:active, &:focus, &:focus-visible": {
              borderColor: "#D9D9D9",
              borderWidth: 2,
              outline: "none",
              boxShadow: "none",
              bgcolor: "#FFFFFF",
            },
          }}
        >
          書類アップロード
        </MuiButton>
      )}

      {/* 統合されたドキュメントリストの表示 */}
      {allDocuments.length > 0 && (
        <Box>
          {allDocuments.map((doc, idx) => (
            <Box
              key={`${doc.isNew ? "new" : "existing"}-${doc.name}-${idx}`}
              sx={{
                background: "#fff",
                border: "1px solid #d9d9d9",
                borderRadius: "10px",
                px: 1.25,
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={documentIcon}
                  width={20}
                  height={20}
                  alt="doc"
                  sx={{ flexShrink: 0 }}
                />
                <Box
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#3e3e3e",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <CustomTruncateText text={doc.name} />
                </Box>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexShrink: 0 }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    if (doc.isNew && doc.file) {
                      handlePreviewClick(doc.file);
                    } else if (!doc.isNew && doc.url) {
                      handlePreviewClick(undefined, doc);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    border: "1px solid #0b9dbd",
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    cursor: "pointer",
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: "#f0f9fb",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Noto Sans JP", sans-serif',
                      fontWeight: 700,
                      fontSize: "10px",
                      color: "#0b9dbd",
                      lineHeight: 1.7,
                      whiteSpace: "nowrap",
                    }}
                  >
                    プレビュー
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => removeDocument(doc.index, doc.isNew)}
                  sx={{
                    color: "#3e3e3e",
                    width: 20,
                    height: 20,
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </Box>
          ))}
          {/* 追加ボタン */}
          <MuiButton
            variant="outlined"
            onClick={() => inputRef.current?.click()}
            sx={{
              width: "100%",
              height: 34,
              fontSize: "12px",
              borderWidth: 1,
              borderColor: "#0B9DBD",
              borderStyle: "dashed",
              color: "#0B9DBD",
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              mt: 1,
              "&:hover": {
                borderColor: "#0B9DBD",
                bgcolor: "#FFFFFF",
              },
              "&:active, &:focus, &:focus-visible": {
                borderColor: "#0B9DBD",
                borderWidth: 1,
                outline: "none",
                boxShadow: "none",
                bgcolor: "#FFFFFF",
              },
            }}
          >
            ＋書類を追加
          </MuiButton>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        style={{ display: "none" }}
        accept={accept}
        multiple={type === "multiple"}
        onChange={handleFileInputChange}
      />

      {hasError && (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "error.main",
          }}
        >
          <AlertCircleIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="error">
            {(hasError?.message as string) || "アップロードエラー"}
          </Typography>
        </Box>
      )}

      {uploadError && (
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "error.main",
          }}
        >
          <AlertCircleIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" color="error">
            {uploadError}
          </Typography>
        </Box>
      )}

      {/* プレビューモーダル */}
      {selectedDocument && (
        <CustomFileModal
          open={modalOpen}
          onClose={handleCloseModal}
          imageSrc={selectedDocument.src}
          imageName={selectedDocument.name}
        />
      )}
    </Box>
  );
};

export default CustomDocumentPreview;

// ========== アコーディオン形式のドキュメントアップロード ==========

export interface DocumentCategory {
  key: string;
  label: string;
}

export interface DocumentGroup {
  key: string;
  label: string;
  categories: DocumentCategory[];
}

export interface DocumentUploadAccordionProps {
  register?: any;
  setValue?: any;
  errors?: any;
  watch?: any; // フォームの値を監視するため
  existingDocuments?: Record<string, string[]>; // keyごとの既存ファイルURL配列
  onDownload?: (doc: DocumentItem) => void;
  defaultExpandedGroups?: string[]; // デフォルトで開くグループ
  focusCategoryLabel?: string; // ラベル一致で該当グループを自動で開く（未一致は「その他」）
  documentGroups: DocumentGroup[]; // ドキュメントグループ（必須）
  // その他カテゴリの動的書類管理
  otherDocuments?: Array<{ key: string; label: string }>; // その他カテゴリの書類リスト
  onAddOtherDocument?: (label: string) => void; // その他書類追加時のコールバック
  onRemoveOtherDocument?: (key: string) => void; // その他書類削除時のコールバック
  onDocumentDeleted?: (key: string, deletedUrl: string, index?: number) => void; // 既存ドキュメント削除時のコールバック
}

export const DocumentUploadAccordion: React.FC<
  DocumentUploadAccordionProps
> = ({
  register,
  setValue,
  errors = {},
  watch,
  existingDocuments = {},
  onDownload,
  defaultExpandedGroups = [],
  focusCategoryLabel,
  documentGroups,
  otherDocuments = [],
  onAddOtherDocument,
  onRemoveOtherDocument,
  onDocumentDeleted,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>(
    defaultExpandedGroups,
  );
  const [newDocumentLabel, setNewDocumentLabel] = useState("");

  React.useEffect(() => {
    if (defaultExpandedGroups.length === 0) return;
    setExpandedGroups(defaultExpandedGroups);
  }, [defaultExpandedGroups.join(",")]);

  React.useEffect(() => {
    if (!focusCategoryLabel) return;

    const trimmed = focusCategoryLabel.trim();
    if (!trimmed) return;

    const findGroupByLabel = (): {
      groupKey: string;
      categoryKey?: string;
    } | null => {
      for (const group of documentGroups) {
        for (const category of group.categories) {
          if (category.label === trimmed) {
            return { groupKey: group.key, categoryKey: category.key };
          }
        }
      }
      return null;
    };

    const found = findGroupByLabel();
    if (found?.groupKey) {
      setExpandedGroups((prev) =>
        prev.includes(found.groupKey) ? prev : [...prev, found.groupKey],
      );
      if (found.categoryKey) {
        requestAnimationFrame(() => {
          const el = document.getElementById(
            `doc-category-${found.categoryKey}`,
          );
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
      return;
    }

    setExpandedGroups((prev) =>
      prev.includes("others") ? prev : [...prev, "others"],
    );
    requestAnimationFrame(() => {
      const el = document.getElementById("doc-category-others");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [focusCategoryLabel, documentGroups]);

  const handleAccordionChange =
    (groupKey: string) => (_: any, isExpanded: boolean) => {
      setExpandedGroups((prev) =>
        isExpanded
          ? [...prev, groupKey]
          : prev.filter((key) => key !== groupKey),
      );
    };

  const handleAddOtherDocument = () => {
    if (newDocumentLabel.trim() && onAddOtherDocument) {
      onAddOtherDocument(newDocumentLabel.trim());
      setNewDocumentLabel("");
    }
  };

  // グループ内の添付ファイル数をカウント
  const getDocumentCount = (group: DocumentGroup): number => {
    let count = 0;
    group.categories.forEach((category) => {
      // watch(key) が既に既存URL + 新規ファイルの混在配列を返すので、それだけをカウント
      if (watch) {
        const uploadedFiles = watch(category.key);
        if (uploadedFiles && Array.isArray(uploadedFiles)) {
          count += uploadedFiles.length;
        }
      }
    });
    return count;
  };

  // その他カテゴリのファイル数をカウント
  const getOtherDocumentCount = (): number => {
    let count = 0;
    otherDocuments.forEach((doc) => {
      // watch(key) が既に既存URL + 新規ファイルの混在配列を返すので、それだけをカウント
      if (watch) {
        const uploadedFiles = watch(doc.key);
        if (uploadedFiles && Array.isArray(uploadedFiles)) {
          count += uploadedFiles.length;
        }
      }
    });
    return count;
  };

  return (
    <Box
      sx={{
        width: "100%",
        // 各 Accordion の間にギャップを確保 (MUI の Mui-expanded margin リセット対策)
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {documentGroups.map((group) => {
        const documentCount = getDocumentCount(group);
        return (
          <Accordion
            key={group.key}
            expanded={expandedGroups.includes(group.key)}
            onChange={handleAccordionChange(group.key)}
            disableGutters
            sx={{
              m: 0,
              "&.Mui-expanded": { m: 0 },
              borderRadius: "10px !important",
              "&:before": { display: "none" },
              border: "1px solid #D9D9D9",
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              expandIcon={null}
              sx={{
                flexDirection: "row",
                position: "relative",
                paddingLeft: 1,
                "& .MuiAccordionSummary-content": {
                  margin: "12px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                },
              }}
            >
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleAccordionChange(group.key)(
                    null,
                    !expandedGroups.includes(group.key),
                  );
                }}
                sx={{
                  width: 26,
                  height: 26,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: expandedGroups.includes(group.key)
                    ? "#DBF8FF"
                    : "transparent",
                  color: expandedGroups.includes(group.key)
                    ? "#0E9DBC"
                    : "#3e3e3e",
                  transition: "all 0.2s",
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: "#DBF8FF",
                    "& svg": {
                      color: "#0E9DBC",
                    },
                  },
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: expandedGroups.includes(group.key)
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                    fontSize: 20,
                    color: "inherit",
                    transition: "transform 0.2s ease",
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                {group.label}
              </Typography>
              {documentCount > 0 && (
                <Chip
                  label={`${documentCount}件添付あり`}
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "11px",
                    fontWeight: 700,
                    backgroundColor: "#E3F2FD",
                    color: "#0B9DBD",
                    cursor: "default",
                    "&:hover": {
                      backgroundColor: "#E3F2FD",
                    },
                  }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {group.categories.map((category) => (
                  <Box key={category.key} id={`doc-category-${category.key}`}>
                    <CustomDocumentPreview
                      name={category.key}
                      label={category.label}
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      errors={errors}
                      type="multiple"
                      existingDocuments={existingDocuments[category.key] || []}
                      onDownload={onDownload}
                      onDocumentDeleted={
                        onDocumentDeleted
                          ? (deletedUrl: string, index?: number) =>
                              onDocumentDeleted(category.key, deletedUrl, index)
                          : undefined
                      }
                    />
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* その他カテゴリ */}
      <Accordion
        expanded={expandedGroups.includes("others")}
        onChange={handleAccordionChange("others")}
        disableGutters
        sx={{
          m: 0,
          "&.Mui-expanded": { m: 0 },
          borderRadius: "10px !important",
          "&:before": { display: "none" },
          border: "1px solid #D9D9D9",
          boxShadow: "none",
        }}
      >
        <AccordionSummary
          expandIcon={null}
          sx={{
            flexDirection: "row",
            position: "relative",
            paddingLeft: 0.5,
            "& .MuiAccordionSummary-content": {
              margin: "12px 0",
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              handleAccordionChange("others")(
                null,
                !expandedGroups.includes("others"),
              );
            }}
            sx={{
              width: 26,
              height: 26,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              cursor: "pointer",
              backgroundColor: expandedGroups.includes("others")
                ? "#DBF8FF"
                : "transparent",
              color: expandedGroups.includes("others") ? "#0E9DBC" : "#3e3e3e",
              transition: "all 0.2s",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#DBF8FF",
                "& svg": {
                  color: "#0E9DBC",
                },
              },
            }}
          >
            <ExpandMoreIcon
              sx={{
                transform: expandedGroups.includes("others")
                  ? "rotate(0deg)"
                  : "rotate(-90deg)",
                fontSize: 20,
                color: "inherit",
                transition: "transform 0.2s ease",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
            その他
          </Typography>
          {getOtherDocumentCount() > 0 && (
            <Chip
              label={`${getOtherDocumentCount()}件添付あり`}
              size="small"
              sx={{
                height: "20px",
                fontSize: "11px",
                fontWeight: 700,
                backgroundColor: "#E3F2FD",
                color: "#0B9DBD",
                cursor: "default",
                "&:hover": {
                  backgroundColor: "#E3F2FD",
                },
              }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 3 }}>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            id="doc-category-others"
          >
            {/* 追加された書類リスト */}
            {otherDocuments.map((doc) => (
              <CustomDocumentPreview
                key={doc.key}
                name={doc.key}
                label={doc.label}
                register={register}
                setValue={setValue}
                watch={watch}
                errors={errors}
                type="multiple"
                existingDocuments={existingDocuments[doc.key] || []}
                onDownload={onDownload}
                onRemove={
                  onRemoveOtherDocument
                    ? () => {
                        onRemoveOtherDocument(doc.key);
                      }
                    : undefined
                }
                onDocumentDeleted={
                  onDocumentDeleted
                    ? (deletedUrl: string, index?: number) =>
                        onDocumentDeleted(doc.key, deletedUrl, index)
                    : undefined
                }
              />
            ))}

            {/* 書類名入力フォーム (支出登録モーダル等と同じパターン: label「カスタムフォーム作成」+ placeholder「項目名を入力」)。
                onAddOtherDocument を渡した時のみ表示 (物件書類はクライアント単位で書類設定ページから管理するため非表示)。 */}
            {onAddOtherDocument && (
              <Box
                sx={{
                  width: { xs: "100%", md: "600px" },
                  mt: otherDocuments.length > 0 ? 1 : 0,
                }}
              >
                <CustomFormCreateInput
                  value={newDocumentLabel}
                  onChange={setNewDocumentLabel}
                  onAdd={handleAddOtherDocument}
                />
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
